import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { inr } from "@/lib/api";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { user, cart, refreshCart } = useAuth();
  const nav = useNavigate();
  if (user === undefined) return <div className="min-h-[60vh] grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return (
    <main className="max-w-3xl mx-auto px-4 py-16 text-center">
      <ShoppingBag size={48} className="mx-auto text-slate-300 mb-4" />
      <h1 className="font-display text-2xl font-bold mb-2">Please login to see your cart</h1>
      <Link data-testid="cart-login-btn" to="/login" className="btn-primary-sf inline-flex px-6 py-2.5 rounded-full text-sm font-semibold mt-4">Login</Link>
    </main>
  );

  const items = cart?.items || [];
  const subtotal = items.reduce((a, i) => a + (i.product?.price || 0) * i.quantity, 0);
  const shipping = subtotal > 999 || subtotal === 0 ? 0 : 49;
  const total = subtotal + shipping;

  const setQty = async (it, delta) => {
    const q = Math.max(1, it.quantity + delta);
    await api.post("/cart/update", { product_id: it.product_id, size: it.size, color: it.color, quantity: q });
    await refreshCart();
  };
  const remove = async (it) => {
    await api.post("/cart/remove", { product_id: it.product_id, size: it.size, color: it.color, quantity: it.quantity });
    await refreshCart();
    toast.success("Removed from cart");
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display text-3xl font-bold mb-6">Your Cart ({items.length})</h1>
      {items.length === 0 ? (
        <div className="text-center py-20">
          <ShoppingBag size={48} className="mx-auto text-slate-300 mb-4"/>
          <div className="font-display text-xl font-semibold">Your cart is empty</div>
          <Link data-testid="cart-shop-btn" to="/products" className="btn-primary-sf inline-flex mt-5 px-6 py-2.5 rounded-full text-sm font-semibold">Continue Shopping</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-3">
            {items.map((it) => {
              const key = `${it.product_id}|${it.size||""}|${it.color||""}`;
              return (
              <div key={key} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 bg-white border border-slate-100 rounded-2xl p-3">
                <Link to={`/product/${it.product_id}`} className="w-24 h-32 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                  <img src={it.product?.images?.[0]} alt="" className="w-full h-full object-cover"/>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.product_id}`} className="font-medium text-slate-900 line-clamp-2">{it.product?.title}</Link>
                  <div className="text-xs text-slate-500 mt-1">{it.size && <>Size: {it.size}</>}{it.color && <> · Color: {it.color}</>}</div>
                  <div className="flex items-center gap-3 mt-3">
                    <div className="inline-flex items-center border border-slate-200 rounded-full">
                      <button data-testid={`qty-dec-${it.product_id}`} onClick={()=>setQty(it,-1)} className="w-8 h-8 grid place-items-center"><Minus size={14}/></button>
                      <span className="px-2 text-sm font-semibold">{it.quantity}</span>
                      <button data-testid={`qty-inc-${it.product_id}`} onClick={()=>setQty(it,1)} className="w-8 h-8 grid place-items-center"><Plus size={14}/></button>
                    </div>
                    <button data-testid={`remove-${it.product_id}`} onClick={()=>remove(it)} className="text-xs text-red-600 inline-flex items-center gap-1"><Trash2 size={13}/> Remove</button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-[#004BCA]">{inr((it.product?.price||0)*it.quantity)}</div>
                  <div className="text-xs line-through text-slate-400">{inr((it.product?.mrp||0)*it.quantity)}</div>
                </div>
              </div>
              );
            })}
          </div>
          <aside className="bg-slate-50 rounded-2xl p-6 h-fit sticky top-24">
            <h3 className="font-display font-bold text-lg mb-4">Order Summary</h3>
            <Row k="Subtotal" v={inr(subtotal)} />
            <Row k="Shipping" v={shipping === 0 ? "FREE" : inr(shipping)} />
            <div className="border-t border-slate-200 my-3"/>
            <Row k={<span className="font-bold">Total</span>} v={<span className="font-display text-lg font-bold text-[#004BCA]">{inr(total)}</span>} />
            <button data-testid="cart-checkout-btn" onClick={() => nav("/checkout")} className="btn-fire w-full mt-5 py-3 rounded-full font-semibold">Proceed to Checkout</button>
            <p className="text-xs text-slate-500 mt-3 text-center">Free shipping on orders above ₹999</p>
          </aside>
        </div>
      )}
    </main>
  );
}

function Row({ k, v }) { return <div className="flex justify-between text-sm mb-2"><span className="text-slate-600">{k}</span><span>{v}</span></div>; }
